import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, Facebook, Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-clay-bg border border-clay-border focus:border-clay-orange outline-none px-4 py-3.5 text-clay-text placeholder:text-clay-muted/60 transition-colors duration-300";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const topic = new URLSearchParams(window.location.search).get("topic");
    if (topic) setForm((f) => ({ ...f, message: f.message || `Sponsorship enquiry: ${topic}\n\n` }));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.message.trim();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20 lg:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          className="lg:col-span-5"
        >
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-clay-orange mb-4">Get in Touch</p>
          <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl leading-none mb-6">
            Talk to the <span className="text-clay-orange">Club</span>
          </h2>
          <p className="text-clay-muted leading-relaxed mb-8 max-w-md">
            Race dates, track details, sponsorship, memberships or anything else — our dedicated club members are
            ready to assist. Your message goes straight to the committee.
          </p>
          <div className="space-y-3">
            <a
              data-testid="contact-email-link"
              href="mailto:midwesternspeedway2@gmail.com"
              className="flex items-center gap-3 text-clay-text hover:text-clay-gold transition-colors text-sm break-all"
            >
              <Mail className="w-5 h-5 text-clay-orange shrink-0" /> midwesternspeedway2@gmail.com
            </a>
            <a
              data-testid="contact-facebook-link"
              href="https://www.facebook.com/share/1QZoHB67yq/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-clay-text hover:text-clay-gold transition-colors text-sm"
            >
              <Facebook className="w-5 h-5 text-clay-orange shrink-0" /> facebook.com/midwesternspeedway
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.12 }}
          className="lg:col-span-7"
        >
          {sent ? (
            <div data-testid="contact-success" className="border border-clay-border bg-clay-card p-10 text-center h-full flex flex-col items-center justify-center">
              <BadgeCheck className="w-14 h-14 text-clay-gold mb-5" />
              <h3 className="font-display font-bold uppercase text-2xl tracking-tight mb-2">Message Sent</h3>
              <p className="text-clay-muted text-sm max-w-sm">
                Thanks {form.name.split(" ")[0]} — the committee has your message and a copy is on its way to your
                inbox. We'll be in touch soon.
              </p>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={submit} className="border border-clay-border bg-clay-card p-6 sm:p-8 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input data-testid="contact-form-name" placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} required />
                <input data-testid="contact-form-phone" placeholder="Phone number" value={form.phone} onChange={set("phone")} className={inputCls} />
              </div>
              <input data-testid="contact-form-email" type="email" placeholder="Email address *" value={form.email} onChange={set("email")} className={inputCls} required />
              <textarea
                data-testid="contact-form-message"
                placeholder="Message *"
                value={form.message}
                onChange={set("message")}
                rows={5}
                className={`${inputCls} resize-none`}
                required
              />
              <button
                data-testid="contact-form-submit-button"
                disabled={!valid || loading}
                className="w-full sm:w-auto bg-clay-orange hover:bg-clay-gold disabled:opacity-40 disabled:cursor-not-allowed text-clay-bg font-display font-bold uppercase tracking-wider text-lg px-10 py-4 transition-all duration-300 flex items-center justify-center gap-2 clay-glow"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
